import { createBrowserRouter } from 'react-router-dom';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import DashboardHome from '@/features/dashboard/pages/DashboardHome';
import LoginPage from '@/features/auth/pages/LoginPage';

/* ── System ── */
import NationalityListPage from '@/features/nationalities/pages/NationalityListPage';
import NationalityCreatePage from '@/features/nationalities/pages/NationalityCreatePage';
import NationalityEditPage from '@/features/nationalities/pages/NationalityEditPage';

import HousingStatusListPage from '@/features/housing-statuses/pages/HousingStatusListPage';
import HousingStatusCreatePage from '@/features/housing-statuses/pages/HousingStatusCreatePage';
import HousingStatusEditPage from '@/features/housing-statuses/pages/HousingStatusEditPage';

import ReferralListPage from '@/features/referrals/pages/ReferralListPage';
import ReferralCreatePage from '@/features/referrals/pages/ReferralCreatePage';
import ReferralEditPage from '@/features/referrals/pages/ReferralEditPage';

import PropertyAmenityListPage from '@/features/property-amenities/pages/PropertyAmenityListPage';
import PropertyAmenityCreatePage from '@/features/property-amenities/pages/PropertyAmenityCreatePage';
import PropertyAmenityEditPage from '@/features/property-amenities/pages/PropertyAmenityEditPage';

import SystemSettingListPage from '@/features/system-settings/pages/SystemSettingListPage';
import SystemSettingCreatePage from '@/features/system-settings/pages/SystemSettingCreatePage';
import SystemSettingEditPage from '@/features/system-settings/pages/SystemSettingEditPage';

/* ── Geography ── */
import CountryListPage from '@/features/countries/pages/CountryListPage';
import CountryCreatePage from '@/features/countries/pages/CountryCreatePage';
import CountryEditPage from '@/features/countries/pages/CountryEditPage';

import RegionListPage from '@/features/regions/pages/RegionListPage';
import RegionCreatePage from '@/features/regions/pages/RegionCreatePage';
import RegionEditPage from '@/features/regions/pages/RegionEditPage';

import CityListPage from '@/features/cities/pages/CityListPage';
import CityCreatePage from '@/features/cities/pages/CityCreatePage';
import CityEditPage from '@/features/cities/pages/CityEditPage';

import DistrictListPage from '@/features/districts/pages/DistrictListPage';
import DistrictCreatePage from '@/features/districts/pages/DistrictCreatePage';
import DistrictEditPage from '@/features/districts/pages/DistrictEditPage';

/* ── Users ── */
import UserListPage from '@/features/users/pages/UserListPage';
import UserEditPage from '@/features/users/pages/UserEditPage';
import UserDetailsPage from '@/features/users/pages/UserDetailsPage';

/* ── Ads ── */
import RoomAdListPage from '@/features/room-ads/pages/RoomAdListPage';
import RoomAdCreatePage from '@/features/room-ads/pages/RoomAdCreatePage';
import RoomAdEditPage from '@/features/room-ads/pages/RoomAdEditPage';
import RoomAdDetailsPage from '@/features/room-ads/pages/RoomAdDetailsPage';

import RoomWantedListPage from '@/features/room-wanted/pages/RoomWantedListPage';
import RoomWantedEditPage from '@/features/room-wanted/pages/RoomWantedEditPage';
import RoomWantedDetailsPage from '@/features/room-wanted/pages/RoomWantedDetailsPage';
import ExternalAdsReviewPage from '@/features/external-ads/pages/ExternalAdsReviewPage';

/* ── Blocked Users ── */
import BlockedUsersListPage from '@/features/blocked-users/pages/BlockedUsersListPage';
import BlockedUsersCreatePage from '@/features/blocked-users/pages/BlockedUsersCreatePage';

/* ── Notifications ── */
import NotificationListPage from '@/features/notifications/pages/NotificationListPage';
import NotificationCreatePage from '@/features/notifications/pages/NotificationCreatePage';
import NotificationEditPage from '@/features/notifications/pages/NotificationEditPage';

/* ── Chat ── */
import ChatRoomListPage from '@/features/chat/pages/ChatRoomListPage';
import MessageListPage from '@/features/chat/pages/MessageListPage';

/* ── Ops (hardened admin features) ── */
import AuditLogsPage from '@/features/ops/pages/AuditLogsPage';
import ReportsPage from '@/features/ops/pages/ReportsPage';
import AnalyticsEventsPage from '@/features/analytics/pages/AnalyticsEventsPage';
import DemandIntelligencePage from '@/features/analytics/pages/DemandIntelligencePage';
import GrowthAnalyticsPage from '@/features/analytics/pages/GrowthAnalyticsPage';

/* ── Import tools ── */
import RoomatyImportPage from '@/features/import/pages/RoomatyImportPage';
import ImportedAdsListPage from '@/features/imported-ads/pages/ImportedAdsListPage';

/* ── AI Ad Creation ── */
import AiAdCreatePage from '@/features/ai-ads/pages/AiAdCreatePage';
import AiAdWhatsAppUpdatesPage from '@/features/ai-ads/pages/AiAdWhatsAppUpdatesPage';
import BlogPostsPage from '@/features/blog/pages/BlogPostsPage';

/* ── Translations (i18n) ── */
import TranslationsListPage from '@/features/translations/pages/TranslationsListPage';
import AdminUsersPage from '@/features/admin-users/pages/AdminUsersPage';
import RolesPage from '@/features/roles/pages/RolesPage';
import PermissionsPage from '@/features/permissions/pages/PermissionsPage';

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: <DashboardLayout />,
    children: [
      { index: true, element: <DashboardHome /> },

      /* Nationalities */
      { path: 'nationalities', element: <NationalityListPage /> },
      { path: 'nationalities/create', element: <NationalityCreatePage /> },
      { path: 'nationalities/:id/edit', element: <NationalityEditPage /> },

      /* Housing Statuses */
      { path: 'housing-statuses', element: <HousingStatusListPage /> },
      { path: 'housing-statuses/create', element: <HousingStatusCreatePage /> },
      { path: 'housing-statuses/:id/edit', element: <HousingStatusEditPage /> },

      /* Referrals */
      { path: 'referrals', element: <ReferralListPage /> },
      { path: 'referrals/create', element: <ReferralCreatePage /> },
      { path: 'referrals/:id/edit', element: <ReferralEditPage /> },

      /* Property Amenities */
      { path: 'property-amenities', element: <PropertyAmenityListPage /> },
      { path: 'property-amenities/create', element: <PropertyAmenityCreatePage /> },
      { path: 'property-amenities/:id/edit', element: <PropertyAmenityEditPage /> },

      /* System Settings */
      { path: 'system-settings', element: <SystemSettingListPage /> },
      { path: 'system-settings/create', element: <SystemSettingCreatePage /> },
      { path: 'system-settings/:id/edit', element: <SystemSettingEditPage /> },

      /* Countries */
      { path: 'countries', element: <CountryListPage /> },
      { path: 'countries/create', element: <CountryCreatePage /> },
      { path: 'countries/:id/edit', element: <CountryEditPage /> },

      /* Regions */
      { path: 'regions', element: <RegionListPage /> },
      { path: 'regions/create', element: <RegionCreatePage /> },
      { path: 'regions/:id/edit', element: <RegionEditPage /> },

      /* Cities */
      { path: 'cities', element: <CityListPage /> },
      { path: 'cities/create', element: <CityCreatePage /> },
      { path: 'cities/:id/edit', element: <CityEditPage /> },

      /* Districts */
      { path: 'districts', element: <DistrictListPage /> },
      { path: 'districts/create', element: <DistrictCreatePage /> },
      { path: 'districts/:id/edit', element: <DistrictEditPage /> },

      /* Users */
      { path: 'users', element: <UserListPage /> },
      { path: 'users/:id', element: <UserDetailsPage /> },
      { path: 'users/:id/edit', element: <UserEditPage /> },

      /* Room Ads */
      { path: 'room-ads', element: <RoomAdListPage /> },
      { path: 'room-ads/new', element: <RoomAdCreatePage /> },
      { path: 'room-ads/:id', element: <RoomAdDetailsPage /> },
      { path: 'room-ads/:id/edit', element: <RoomAdEditPage /> },

      /* Room Wanted Ads */
      { path: 'room-wanted', element: <RoomWantedListPage /> },
      { path: 'room-wanted/:id', element: <RoomWantedDetailsPage /> },
      { path: 'room-wanted/:id/edit', element: <RoomWantedEditPage /> },
      { path: 'external-ads', element: <ExternalAdsReviewPage /> },

      /* Blocked Users */
      { path: 'blocked-users', element: <BlockedUsersListPage /> },
      { path: 'blocked-users/create', element: <BlockedUsersCreatePage /> },

      /* Notifications */
      { path: 'notifications', element: <NotificationListPage /> },
      { path: 'notifications/create', element: <NotificationCreatePage /> },
      { path: 'notifications/:id/edit', element: <NotificationEditPage /> },

      /* Chat */
      { path: 'chat/rooms', element: <ChatRoomListPage /> },
      { path: 'chat/messages', element: <MessageListPage /> },

      /* Ops */
      { path: 'audit-logs', element: <AuditLogsPage /> },
      { path: 'analytics/events', element: <AnalyticsEventsPage /> },
      { path: 'analytics/growth', element: <GrowthAnalyticsPage section="overview" /> },
      { path: 'analytics/funnels', element: <GrowthAnalyticsPage section="funnels" /> },
      { path: 'analytics/search', element: <GrowthAnalyticsPage section="search" /> },
      { path: 'analytics/demand-intelligence', element: <DemandIntelligencePage /> },
      { path: 'analytics/marketplace', element: <GrowthAnalyticsPage section="marketplace" /> },
      { path: 'analytics/attribution', element: <GrowthAnalyticsPage section="attribution" /> },
      { path: 'analytics/retention', element: <GrowthAnalyticsPage section="retention" /> },
      { path: 'reports', element: <ReportsPage /> },

      /* Import tools */
      { path: 'import/roomaty', element: <RoomatyImportPage /> },
      { path: 'imported-ads', element: <ImportedAdsListPage /> },

      /* AI Ad Creation */
      { path: 'ai-create', element: <AiAdCreatePage /> },
      { path: 'ai-whatsapp-updates', element: <AiAdWhatsAppUpdatesPage /> },
      { path: 'blog-posts', element: <BlogPostsPage /> },

      /* Translations (i18n) */
      { path: 'admin/translations', element: <TranslationsListPage /> },
      { path: 'admin/users', element: <AdminUsersPage /> },
      { path: 'admin/roles', element: <RolesPage /> },
      { path: 'admin/permissions', element: <PermissionsPage /> },

      /* Admin & Access compatibility routes used by the production dashboard URLs */
      { path: 'admin-users', element: <AdminUsersPage /> },
      { path: 'roles', element: <RolesPage /> },
      { path: 'permissions', element: <PermissionsPage /> },
    ],
  },
]);
